import React, { useEffect, useMemo, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import pocketbaseService from '../../services/pocketbase';
import { useApp } from '../../contexts/AppContext';
import { TYPE_COLORS, SEVERITY_COLORS } from '../../config/constants';
import { formatDistanceToNow } from 'date-fns';

const { FiInbox, FiAlertCircle, FiClock, FiCheckCircle, FiPauseCircle, FiAlertTriangle, FiUserX, FiGrid, FiRefreshCw, FiArrowRight } = FiIcons;

const Dashboard = ({ refreshKey, onNavigateToInbox, onTicketSelect }) => {
  const { selectedApp, apps, showToast, setSelectedApp } = useApp();
  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState({ counts: {}, recent: [], windowItems: [] });

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const data = await pocketbaseService.getDashboardSnapshot(selectedApp);
      setSnapshot(data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      showToast('Failed to load dashboard', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDashboard(); }, [selectedApp, refreshKey]);

  const byApp = useMemo(() => {
    const map = {};
    snapshot.windowItems.forEach((ticket) => {
      const appName = ticket.expand?.app?.appName || 'Unknown App';
      if (!map[appName]) map[appName] = { total: 0, bugs: 0, features: 0 };
      map[appName].total += 1;
      if (ticket.type === 'Bug Report') map[appName].bugs += 1;
      if (ticket.type === 'Feature Request') map[appName].features += 1;
    });
    return Object.entries(map).map(([name, values]) => ({ name, ...values })).sort((a, b) => b.total - a.total);
  }, [snapshot.windowItems]);

  const byType = useMemo(() => {
    const keys = ['Bug Report', 'Feature Request', 'Billing', 'How To', 'Other'];
    return keys.map((type) => ({ label: type, value: snapshot.windowItems.filter((ticket) => ticket.type === type).length }));
  }, [snapshot.windowItems]);

  const bySeverity = useMemo(() => {
    const keys = ['Critical', 'High', 'Medium', 'Low'];
    return keys.map((severity) => ({ label: severity, value: snapshot.windowItems.filter((ticket) => ticket.severity === severity).length }));
  }, [snapshot.windowItems]);

  const maxType = Math.max(1, ...byType.map((item) => item.value));
  const maxSeverity = Math.max(1, ...bySeverity.map((item) => item.value));
  const maxApp = Math.max(1, ...byApp.map((item) => item.total));

  const cards = [
    { label: 'Open Tickets', value: snapshot.counts.open || 0, icon: FiInbox, tone: 'bg-blue-50 text-blue-700 border-blue-200', preset: { statuses: ['New', 'Triage', 'In Progress', 'Waiting on Customer'], dateRange: 'all' } },
    { label: 'New / Untriaged', value: snapshot.counts.new || 0, icon: FiAlertCircle, tone: 'bg-purple-50 text-purple-700 border-purple-200', preset: { statuses: ['New'], dateRange: 'all' } },
    { label: 'In Progress', value: snapshot.counts.inProgress || 0, icon: FiClock, tone: 'bg-amber-50 text-amber-700 border-amber-200', preset: { statuses: ['In Progress'], dateRange: 'all' } },
    { label: 'Waiting on Customer', value: snapshot.counts.waiting || 0, icon: FiPauseCircle, tone: 'bg-orange-50 text-orange-700 border-orange-200', preset: { statuses: ['Waiting on Customer'], dateRange: 'all' } },
    { label: 'Resolved', value: snapshot.counts.resolved || 0, icon: FiCheckCircle, tone: 'bg-emerald-50 text-emerald-700 border-emerald-200', preset: { statuses: ['Resolved'], dateRange: 'all' } },
    { label: 'High / Critical', value: snapshot.counts.highCritical || 0, icon: FiAlertTriangle, tone: 'bg-red-50 text-red-700 border-red-200', preset: { severity: 'High,Critical', dateRange: 'all' } },
    { label: 'Unassigned', value: snapshot.counts.unassigned || 0, icon: FiUserX, tone: 'bg-slate-50 text-slate-700 border-slate-200', preset: { unassigned: true, dateRange: 'all' } },
    { label: 'Active Apps', value: apps.filter((app) => app.isActive !== false).length, icon: FiGrid, tone: 'bg-indigo-50 text-indigo-700 border-indigo-200', preset: null }
  ];

  const handleAppClick = (appName) => { setSelectedApp(appName); onNavigateToInbox({ dateRange: '90' }); };

  if (loading) return <div className="h-full flex items-center justify-center"><div className="animate-spin rounded-full h-9 w-9 border-b-2 border-blue-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
          <p className="text-sm text-gray-500 mt-1">Support operations overview{selectedApp !== 'All Apps' ? ` for ${selectedApp}` : ''}. Breakdown panels use the most recent 90 days.</p>
        </div>
        <button type="button" onClick={loadDashboard} className="inline-flex items-center px-3 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
          <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {cards.map((card) => (
          <button key={card.label} type="button" onClick={() => card.preset && onNavigateToInbox(card.preset)} disabled={!card.preset} className={`text-left rounded-xl border p-5 transition-all ${card.tone} ${card.preset ? 'hover:-translate-y-0.5 hover:shadow-md cursor-pointer' : 'cursor-default'}`}>
            <div className="flex items-start justify-between"><div><p className="text-sm font-semibold opacity-80">{card.label}</p><p className="text-3xl font-bold mt-2">{card.value}</p></div><SafeIcon icon={card.icon} className="w-6 h-6" /></div>
            {card.preset && <div className="flex items-center text-xs font-semibold mt-4 opacity-80">View tickets <SafeIcon icon={FiArrowRight} className="w-3 h-3 ml-1" /></div>}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-gray-900">Tickets by App</h3><span className="text-xs text-gray-500">Last 90 days</span></div>
          <div className="space-y-4">
            {byApp.length > 0 ? byApp.map((item) => (
              <button key={item.name} type="button" onClick={() => handleAppClick(item.name)} className="w-full text-left group">
                <div className="flex items-center justify-between text-sm mb-1.5"><span className="font-medium text-gray-800 group-hover:text-blue-700">{item.name}</span><span className="text-gray-500">{item.total} tickets</span></div>
                <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full bg-blue-600" style={{ width: `${Math.max(6, (item.total / maxApp) * 100)}%` }} /></div>
                <div className="flex gap-3 mt-1.5 text-xs text-gray-500"><span>{item.bugs} bugs</span><span>{item.features} feature requests</span></div>
              </button>
            )) : <p className="text-sm text-gray-500">No recent ticket activity.</p>}
          </div>
        </section>

        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-gray-900">Recent Activity</h3><button type="button" onClick={() => onNavigateToInbox({})} className="text-sm font-medium text-blue-600 hover:text-blue-800">View Inbox</button></div>
          <div className="divide-y divide-gray-100">
            {snapshot.recent.length > 0 ? snapshot.recent.map((ticket) => (
              <button key={ticket.id} type="button" onClick={() => onTicketSelect(ticket)} className="w-full text-left py-3 hover:bg-gray-50 px-2 -mx-2 rounded-lg">
                <div className="flex items-start justify-between gap-3"><div className="min-w-0"><div className="flex items-center gap-2"><span className="font-mono text-xs font-semibold text-blue-600">{ticket.ticketRef}</span><span className="text-xs text-gray-400">{ticket.expand?.app?.appName || 'Unknown App'}</span></div><p className="text-sm font-medium text-gray-900 truncate mt-1">{ticket.subject}</p></div><span className="text-xs text-gray-500 whitespace-nowrap">{formatDistanceToNow(new Date(ticket.updated), { addSuffix: true })}</span></div>
              </button>
            )) : <p className="text-sm text-gray-500 py-4">No recent tickets.</p>}
          </div>
        </section>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="font-bold text-gray-900 mb-4">Tickets by Type</h3>
          <div className="space-y-3">{byType.map((item) => <button key={item.label} type="button" onClick={() => onNavigateToInbox({ type: item.label, dateRange: '90' })} className="w-full text-left group"><div className="flex justify-between items-center text-sm mb-1"><span className={`inline-flex px-2 py-0.5 rounded-full border text-xs font-medium ${TYPE_COLORS[item.label]}`}>{item.label}</span><span className="font-semibold text-gray-700">{item.value}</span></div><div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full bg-blue-500" style={{ width: `${item.value ? Math.max(6, (item.value / maxType) * 100) : 0}%` }} /></div></button>)}</div>
        </section>
        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="font-bold text-gray-900 mb-4">Tickets by Severity</h3>
          <div className="space-y-3">{bySeverity.map((item) => <button key={item.label} type="button" onClick={() => onNavigateToInbox({ severity: item.label, dateRange: '90' })} className="w-full text-left group"><div className="flex justify-between items-center text-sm mb-1"><span className={`inline-flex px-2 py-0.5 rounded-full border text-xs font-medium ${SEVERITY_COLORS[item.label]}`}>{item.label}</span><span className="font-semibold text-gray-700">{item.value}</span></div><div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full bg-red-500" style={{ width: `${item.value ? Math.max(6, (item.value / maxSeverity) * 100) : 0}%` }} /></div></button>)}</div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;